import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { form, required } from '@angular/forms/signals'
import { Tilikausi } from '@lemon/core-firebase/model/kayttaja'
import { TilinpaatosMetadata, TilinpaatosUserData } from '@lemon/core-firebase/model/tilinpaatos'
import { TilinpaatosUriService } from '@lemon/core-firebase/service/tilinpaatos-uri.service'
import { DateService } from '@lemon/core/service/date.service'
import { KayttajaService } from 'app/_angular/service/kayttaja.service'
import { BehaviorSubject, Observable, ReplaySubject, Subject, combineLatest, filter, firstValueFrom, map, of as observableOf, of, startWith, switchMap, takeUntil } from 'rxjs'

import { MatTabGroup } from '@angular/material/tabs'
import { DomSanitizer } from '@angular/platform-browser'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { PdfOpenData, PdfService } from 'app/_angular/service/pdf.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'
import { DocumentSnapshot } from 'firebase/firestore'

export interface ImageCacheClass {
  getImageObservable(): Observable<string>
  startImageRendering(): Promise<boolean>
}

export class ImageCacheClassPdfiumRenderer implements ImageCacheClass {
  private _obsrvbl: ReplaySubject<string> = new ReplaySubject(1)
  private _rendered: boolean = false
  constructor(
    private _pdfService: PdfService,
    private _openData: Pick<PdfOpenData, 'fileAsUint8Array'>,
    private _domSanitizer: DomSanitizer,
    private _index: number,
  ) {}
  getImageObservable(): Observable<string> {
    return this._obsrvbl
  }
  async startImageRendering() {
    if (!this._rendered) {
      this._rendered = true
      const rendered = await this._pdfService.renderPageToObjectUrl(this._index, this._openData.fileAsUint8Array, 600)
      this._obsrvbl.next(this._domSanitizer.bypassSecurityTrustUrl(rendered) as string)
      await this._sleep(50)
      return true
    }
    return false
  }
  private _sleep(millis: number): Promise<any> {
    return new Promise(resolve => {
      setTimeout(resolve, millis)
    })
  }
}

export class ImageCacheClassPreRendered implements ImageCacheClass {
  private _rendered: Observable<string>
  private _renderedCalled: boolean = false
  constructor(rendered: string) {
    this._rendered = of<string>(rendered)
  }
  getImageObservable(): Observable<string> {
    return this._rendered
  }
  async startImageRendering() {
    if (this._renderedCalled) {
      return false
    }
    this._renderedCalled = true
    return true
  }
}

interface TilikausiLomake {
  selectedTilikausi: Tilikausi | null
}

export type ReportsForcedTabChange = 'tilinpaatos' | 'yhtiokokouksen-poytakirja'

export const FORCE_TAB_CHANGE_CHANNEL = 'force-reports-tab-change'

@Component({
  templateUrl: './tilinpaatokset.component.html',
  styleUrls: ['./tilinpaatokset.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TilinpaatoksetComponent implements OnInit, AfterViewInit, OnDestroy {
  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)

  tilikaudetObservable: Observable<Tilikausi[]>
  selectedTilikausiObservable: BehaviorSubject<Tilikausi> = new BehaviorSubject(null)
  tilinpaatosSnapshotObservable: Observable<{ tilikausi: Tilikausi; snap: DocumentSnapshot<TilinpaatosMetadata> }>
  tilinpaatosUsersObservable: Observable<TilinpaatosUserData>

  lomake = form(signal<TilikausiLomake>({ selectedTilikausi: null }), polku => {
    required(polku.selectedTilikausi)
  })

  @ViewChild('tabGroup') reportsTabGroup: MatTabGroup
  readonly valittuValilehti = signal(0)

  private _reportsForceTabChangeChannel: BroadcastChannel

  constructor(
    private _kayttajaService: KayttajaService,
    private _dateService: DateService,
    private _tilinpaatosUriService: TilinpaatosUriService,
    private _firebaseLemonaid: FirebaseLemonaid,
  ) {}

  ngOnInit() {
    this._reportsForceTabChangeChannel = new BroadcastChannel(FORCE_TAB_CHANGE_CHANNEL)

    this._reportsForceTabChangeChannel.addEventListener('message', async (event: MessageEvent<ReportsForcedTabChange>) => {
      const data = event.data
      if (!data || !this.reportsTabGroup) {
        return
      }

      if (data === 'tilinpaatos') {
        this.valittuValilehti.set(0)
      }
      if (data === 'yhtiokokouksen-poytakirja') {
        this.valittuValilehti.set(1)
      }
    })

    this.tilikaudetObservable = this._kayttajaService.nykyinenAsiakasObservable.pipe(
      map(asiakas => {
        if (asiakas?.tilikaudet?.length) {
          return asiakas.tilikaudet
        }
        return null
      }),
    )

    this.tilinpaatosUsersObservable = this._kayttajaService.kayttajanTiedotObservable.pipe(
      switchMap(kayttaja => {
        if (!kayttaja) {
          return observableOf<TilinpaatosUserData>(null)
        }
        return this._firebaseLemonaid.firestoreDoc<TilinpaatosUserData>(this._tilinpaatosUriService.getTilinpaatosUsersDocUri(kayttaja.asiakasAvain)).listen()
      }),
    )

    // this.isTilinpaatosVisibleObservable = this._kayttajaService.kayttajanTiedotObservable.pipe(
    //   switchMap(kayttaja => {
    //     if (!kayttaja) {
    //       return observableOf<boolean>(false)
    //     }
    //     if (kayttaja.roolit.HALLINTO_YHTEYSHENKILO) {
    //       return observableOf<boolean>(true)
    //     }
    //     if (kayttaja.kayttajaTunnistettu === 'tunnistettu-nets') {
    //       return this.tilinpaatosUsersObservable.pipe(
    //         map(boardMembers => {
    //           if (!boardMembers?.hallitusUsers?.length) {
    //             return false
    //           }
    //           const userAsBoardMember = boardMembers.hallitusUsers.find(boardMember => boardMember.lemonaidUid === kayttaja.avain)
    //           if (
    //             userAsBoardMember &&
    //             (
    //               userAsBoardMember.role === 'toimitusjohtaja' ||
    //               userAsBoardMember.role === 'jasen' ||
    //               userAsBoardMember.role === 'puheenjohtaja'
    //             )
    //           ) {
    //             return true
    //           }
    //           return false
    //         })
    //       )
    //     }
    //     return observableOf<boolean>(false)
    //   })
    // )

    arvonMuutokset(this.lomake.selectedTilikausi, { injector: this._injector })
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(tilikausi => {
        // We send here null, so all observables return to the correct state
        // and as a consecuense show loading etc. indicators right
        this.selectedTilikausiObservable.next(null)
        this.selectedTilikausiObservable.next(tilikausi)
      })

    this.tilinpaatosSnapshotObservable = combineLatest([this._kayttajaService.kayttajanTiedotObservable, this.selectedTilikausiObservable]).pipe(
      switchMap(([kayttaja, tilikausi]) => {
        if (!kayttaja || !tilikausi) {
          return observableOf<{ tilikausi: Tilikausi; snap: DocumentSnapshot<TilinpaatosMetadata> }>(null)
        }
        const metadataUri = this._tilinpaatosUriService.getTilinpaatosMetadataDocUri(kayttaja.asiakasAvain, tilikausi.avain)
        return this._firebaseLemonaid
          .firestoreDoc<TilinpaatosMetadata>(metadataUri)
          .listenSnap()
          .pipe(
            map(snap => {
              return { tilikausi: tilikausi, snap: snap }
            }),
          )
      }),
      startWith(null),
      lemonShare(),
    )
  }

  ngAfterViewInit() {
    firstValueFrom(this.tilikaudetObservable.pipe(filter(tilikaudet => !!tilikaudet?.length))).then(tilikaudet => {
      const now = this._dateService.currentLocalDate()
      const yearAgo = this._dateService.lisaaKuukausiaPaikallinen(now, -12)
      // Find the one that has ended less than a year ago
      const endedDuringLastYear = tilikaudet.find(t => this._dateService.onkoLocalDateKahdenValissa(t.loppuu, yearAgo, now))
      if (endedDuringLastYear) {
        asetaArvo(this.lomake.selectedTilikausi, endedDuringLastYear)
        return
      }
      const prevYear = tilikaudet.find(t => t.loppuu.year === now.year - 1)
      if (prevYear) {
        asetaArvo(this.lomake.selectedTilikausi, prevYear)
        return
      }
      const currYear = tilikaudet.find(t => t.loppuu.year === now.year)
      if (currYear) {
        asetaArvo(this.lomake.selectedTilikausi, currYear)
        return
      }
      asetaArvo(this.lomake.selectedTilikausi, tilikaudet[tilikaudet.length - 1])
    })
  }

  compareWithKey(tilikausi1: Tilikausi, tilikausi2: Tilikausi): boolean {
    return tilikausi1?.avain === tilikausi2?.avain
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
